import Loading from "@/components/loading/loading";
import {
  enterStorePopupModeIfRequested,
  isStoreMockMode,
  isStorePopupMode,
  notifyStoreSessionExpired,
} from "@/store/store-config";
import { storeHttpClient } from "@/store/init/initialize-store-instances";
import { clearUserRoleCookie, getUserRoleFromCookie } from "@/utils/cookie-utils";
import { getRoleFromString, UserRole } from "@/utils/enum/user-role";
import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";
import StoreFooter from "./_component/store-footer";
import StoreHeader from "./_component/store-header";
import styles from "./store-layout.module.css";

/** 스토어에 접근 가능한 역할 (의사 계정 그대로 사용, 간호사 포함) */
const STORE_ALLOWED_ROLES: UserRole[] = [UserRole.Doctor, UserRole.Nurse];

/**
 * 스토어 공통 레이아웃
 * - 인증 가드: 역할 쿠키(UI 힌트)만 믿지 않고, 의료 서버에 스토어 토큰 발급을 실제로 시도해 세션을 확인한다.
 *   로그인 쿠키가 없거나 만료됐으면 401 → 스토어 로그인으로 보낸다.
 * - 스토어 전용 헤더/푸터 (PG 심사 필수 사업자 정보 표기)
 */
export default function StoreLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    // 치료 등록 화면에서 새 창으로 열린 경우(popup=1) 이 창을 팝업 모드로 표시 — 이후 내부 이동에도 유지
    enterStorePopupModeIfRequested(location.search);
    /**
     * 로그인 없음/만료 처리.
     * 팝업이면 로그인 화면을 팝업 안에 띄우지 않고 원래 창(치료 기간 설정)에 알려 그 창이 평소처럼 로그인으로 가게 하고 닫는다.
     * 일반 진입이면 메인 로그인(/)으로 보낸다.
     */
    const goToLogin = () => {
      clearUserRoleCookie();
      storeHttpClient.clearToken();
      if (isStorePopupMode() && notifyStoreSessionExpired()) {
        window.close();
        return;
      }
      navigate("/", { replace: true });
    };
    const checkAuth = async () => {
      // 목 모드: 백엔드 없이 화면 확인이 가능하도록 인증 가드를 우회한다.
      if (isStoreMockMode()) {
        setHasAccess(true);
        setIsCheckingAuth(false);
        return;
      }
      try {
        const roleString = await getUserRoleFromCookie();
        if (!roleString) {
          console.log("[StoreLayout] :: 로그인 정보 없음 - 로그인으로 이동");
          goToLogin();
          return;
        }
        const role = getRoleFromString(roleString);
        if (!STORE_ALLOWED_ROLES.includes(role)) {
          console.warn("[StoreLayout] :: 스토어 접근 권한 없는 역할:", role);
          navigate("/", { replace: true });
          return;
        }
        // 실제 세션 확인 — 스토어 토큰 발급이 되어야 스토어 API 를 쓸 수 있다
        await storeHttpClient.ensureToken();
        setHasAccess(true);
      } catch (error) {
        // 로그인 쿠키 없음/만료 (401) 또는 서버 불가 — 남아 있는 역할 쿠키는 지우고 로그인으로
        console.error("[StoreLayout] :: 세션 확인 실패 - 로그인으로 이동:", error);
        goToLogin();
      } finally {
        setIsCheckingAuth(false);
      }
    };
    checkAuth();
  }, [navigate]);

  if (isCheckingAuth || !hasAccess) {
    return <Loading />;
  }

  return (
    <div className={styles.storeContainer}>
      {/* 치료 기간 설정에서 열린 팝업은 헤더(로고·스토어 제목·사용자 메뉴) 없이 본문만. 푸터(사업자 정보)는 PG 심사 표기용으로 유지 */}
      {!isStorePopupMode() && <StoreHeader />}
      <main className={styles.storeMain}>
        <Outlet />
      </main>
      <StoreFooter />
    </div>
  );
}
